import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Form, Input } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { rules } from 'common/helpers/form.helper';
import { EFormFieldMessage } from 'common/const/form.enum';
import { normalizePhone, phoneFormatter } from 'common/helpers';
import { PHONE_PATTERN } from 'common/config';
import { useCaptcha } from 'common/hooks/useCaptcha';

const COOLDOWN_SECONDS = 60;

interface IComponentProps {
  open: boolean;
  type: 'email' | 'phone';
  error: string | null;
  loading: boolean;
  onClose: () => void;
  onSendCode: (value: string, onSuccess: () => void) => Promise<void>;
  onConfirm: (value: string, code: string, onSuccess: () => void) => void;
}

export const ChangeContactCard: FC<IComponentProps> = ({ open, type, error, loading, onClose, onSendCode, onConfirm }) => {
  const [form] = useForm();
  const [codeSent, setCodeSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { trigger, loading: captchaLoading, captchaNode } = useCaptcha();

  useEffect(() => {
    if (error) {
      form.setFields([{ name: 'code', errors: [error] }]);
    }
  }, [error]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setCodeSent(false);
      setCooldown(0);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const isEmail = type === 'email';
  const title = isEmail ? 'Изменение электронной почты' : 'Изменение телефона';
  const codeSentLabel = isEmail ? 'Код отправлен на email' : 'Код отправлен по SMS';

  const startCooldown = () => {
    setCooldown(COOLDOWN_SECONDS);

    intervalRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = (): void => {
    trigger(async () => {
      const values = await form.validateFields(['newValue']);
      const normalizedValue = type === 'phone' ? normalizePhone(values.newValue) : values.newValue;

      await onSendCode(normalizedValue, () => {
        setCodeSent(true);
        startCooldown();
      });
    });
  };

  return (
    <Drawer rootClassName="redesign drawer" open={open} onClose={loading ? undefined : onClose} width={720} destroyOnClose>
      <div className="drawer__title">{title}</div>

      <div className="drawer__body">
        <Form
          layout="vertical"
          form={form}
          requiredMark={false}
          onFinish={({ newValue, code }) => {
            const normalizedValue = type === 'phone' ? normalizePhone(newValue) : newValue;

            onConfirm(normalizedValue, code, () => {
              form.resetFields();
              setCodeSent(false);
              setCooldown(0);

              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
              }
            });
          }}
        >
          {isEmail ? (
            <Form.Item
              name="newValue"
              label="Новый адрес электронной почты"
              rules={[rules.required(), rules.type('email', EFormFieldMessage.InvalidEmailFormat)]}
            >
              <Input placeholder="Введите новый адрес электронной почты" />
            </Form.Item>
          ) : (
            <Form.Item
              name="newValue"
              label="Новый номер телефона"
              normalize={phoneFormatter}
              rules={[rules.required(), { pattern: PHONE_PATTERN, message: EFormFieldMessage.InvalidPhoneFormat }]}
            >
              <Input placeholder="+7 (___) ___-__-__" />
            </Form.Item>
          )}

          <Form.Item className="mb-9" label="Код подтверждения">
            <div className="flex align-center gap-4 mb-4">
              <Button
                className="button-sm secondary"
                onClick={handleSendCode}
                loading={captchaLoading && !codeSent}
                disabled={cooldown > 0}
              >
                {cooldown > 0 ? `Получить код (${cooldown})` : 'Получить код'}
              </Button>

              {codeSent && cooldown > 0 && <span className="ff-regular fs-xs color-dark-grey">{codeSentLabel}</span>}
            </div>

            <Form.Item className="mb-0" name="code" rules={[rules.required()]}>
              <Input placeholder="Введите код" />
            </Form.Item>
          </Form.Item>

          {captchaNode}
        </Form>
      </div>

      <div className="drawer__footer">
        <Button className="button-lg primary" onClick={() => form.submit()} loading={loading}>
          Подтвердить
        </Button>

        <Button className="button-lg secondary" onClick={onClose} disabled={loading}>
          Отмена
        </Button>
      </div>
    </Drawer>
  );
};
